import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { Button, Spin } from 'antd';

import { useCustomerConfigQuery } from '../hooks/queries/useCustomerConfig';
import type { ParsedCustomerConfig } from '../hooks/queries/useCustomerConfig';
import { ConfigValidationError } from '../config/schema/customerConfigSchema';
import { createKpiHelpers } from '../PlanDetail/MetricPage/config/kpiCatalog';
import type { KpiOption } from '../PlanDetail/MetricPage/config/kpiCatalog';
import type { TpoGenConfig } from '../config/adapters/parseTpoGenConfig';

export interface CustomerConfigContextValue extends ParsedCustomerConfig {
    getKpiByKey: (key: string) => KpiOption | undefined;
    getKpiName: (key: string) => string;
}

const CustomerConfigContext = createContext<CustomerConfigContextValue | null>(null);

const FULL_HEIGHT_STYLE = { minHeight: 'calc(100vh - 200px)' } as const;

interface ConfigGateErrorProps {
    error: unknown;
    onRetry: () => void;
}

const ConfigGateError = ({ error, onRetry }: ConfigGateErrorProps) => {
    const isValidationError = error instanceof ConfigValidationError;
    const title = isValidationError ? 'Configuration is invalid' : 'Unable to load configuration';
    const description = isValidationError
        ? 'The planning configuration returned by the server is missing required fields. Access is blocked to avoid showing incorrect data. Please contact support.'
        : 'We could not load the planning configuration. Check your connection and try again.';

    return (
        <div
            className="flex flex-col items-center justify-center text-center gap-y-3 bg-white rounded"
            style={FULL_HEIGHT_STYLE}
        >
            <p className="text-lg font-semibold text-[#18181B]">{title}</p>
            {/* <p className="text-sm text-[#52525C] max-w-[28rem]">{description}</p> */}
            {isValidationError && (error as ConfigValidationError).issues.length > 0 && (
                <ul className="text-xs text-[#9F1239] max-w-[32rem] text-left list-disc pl-5">
                    {(error as ConfigValidationError).issues.slice(0, 8).map((issue, idx) => (
                        <li key={`${issue.path.join('.')}-${idx}`}>
                            {(issue.path.join('.') || '(root)') + ': ' + issue.message}
                        </li>
                    ))}
                </ul>
            )}
            {!isValidationError && (
                <Button type="primary" onClick={onRetry}>
                    Retry
                </Button>
            )}
        </div>
    );
};

export function CustomerConfigProvider({ children }: { children: ReactNode }) {
    const { data, isPending, isError, error, refetch } = useCustomerConfigQuery();

    const value = useMemo<CustomerConfigContextValue | null>(() => {
        if (!data) return null;
        return { ...data, ...createKpiHelpers(data.kpiCatalog) };
    }, [data]);

    if (isPending) {
        return (
            <div className="flex items-center justify-center bg-white" style={FULL_HEIGHT_STYLE}>
                <Spin size="default" />
            </div>
        );
    }

    if (isError || !value) {
        return <ConfigGateError error={error} onRetry={() => refetch()} />;
    }

    return <CustomerConfigContext.Provider value={value}>{children}</CustomerConfigContext.Provider>;
}

export function useCustomerConfig(): CustomerConfigContextValue {
    const ctx = useContext(CustomerConfigContext);
    if (!ctx) {
        throw new Error('useCustomerConfig must be used within a CustomerConfigProvider (TPO Gen routes only)');
    }
    return ctx;
}

export function useTpoGenConfig(): TpoGenConfig {
    return useCustomerConfig().tpoGenConfig;
}
